/**
 * InviteExpiredError Component
 * Feature: 012-care-seeker-invite-signup
 *
 * Error state displayed when an invite link is invalid, expired, or already used.
 * Green background layout matching the legacy SignUpPage style.
 */

import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import SignupBackground from '@/assets/login_background.png';

type InviteErrorType = 'not_found' | 'already_used' | 'expired' | 'unknown';

type InviteExpiredErrorProps = {
  errorType?: InviteErrorType;
  message?: string;
  onRetry?: () => void;
  showRetry?: boolean;
};

const errorContent: Record<InviteErrorType, { title: string; description: string }> = {
  not_found: {
    title: 'Invitation Not Found',
    description:
      'This invitation link appears to be invalid or may have been removed. Please check the link or contact the person who invited you.',
  },
  already_used: {
    title: 'Invitation Already Used',
    description:
      'This invitation has already been accepted. If you have already registered, please sign in to your account.',
  },
  expired: {
    title: 'Invitation Expired',
    description:
      'This invitation has expired. Please contact the person who invited you to request a new invitation.',
  },
  unknown: {
    title: 'Something Went Wrong',
    description:
      'We encountered an error loading this invitation. Please try again or contact support if the problem persists.',
  },
};

export const InviteExpiredError = ({
  errorType = 'unknown',
  message,
  onRetry,
  showRetry = false,
}: InviteExpiredErrorProps) => {
  const content = errorContent[errorType];

  return (
    <div className="bg-[#026857]">
      <main className="bg-[#026857] md:container py-12 flex max-md:flex-col items-center gap-16">
        <section className="md:w-2/4 py-12 max-md:container">
          <h2 className="font-recoleta text-white md:w-full">
            {content.title}
          </h2>

          <p className="font-termina text-white max-md:text-xl max-md:py-8 py-6">
            {message || content.description}
          </p>

          <div className="py-8 gap-4 flex flex-col">
            {showRetry && onRetry && (
              <button
                onClick={onRetry}
                className="font-termina bg-[#E2A807] font-black rounded-3xl py-3 px-6 w-max uppercase cursor-pointer hover:bg-[#c99006] active:scale-[0.98] transition-all duration-150 flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Try Again
              </button>
            )}

            <Link
              to="/login"
              className="font-termina bg-[#E2A807] font-black rounded-3xl py-3 px-6 w-max uppercase text-center hover:bg-[#c99006] active:scale-[0.98] transition-all duration-150"
            >
              Sign In
            </Link>

            {errorType !== 'already_used' && (
              <Link
                to="/register"
                className="font-termina text-white font-bold rounded-3xl py-3 px-6 w-max uppercase border border-white hover:bg-white/10 transition-all duration-150"
              >
                Register Without Invitation
              </Link>
            )}

            <Link
              to="/"
              className="font-termina text-white hover:font-bold underline mt-4"
            >
              Return to Homepage
            </Link>
          </div>
        </section>

        <section className="rounded-2xl p-2 max-md:mb-24">
          <img
            src={SignupBackground}
            alt="signup_background"
            className="rounded-2xl bg-cover bg-center w-full flex-1"
          />
        </section>
      </main>
    </div>
  );
};
